import { createSelector } from 'reselect';
import {
  BUY,
  CASH_APPROACH,
  CREATED,
  FinancialInstrument,
  ForwardContract,
  DURATION_INTERVALS_NAME,
  GROUP_OF_ASSET,
  instrumentTypes,
  OrderExecutionError,
  REGIONS,
  selectors,
  utils,
  Currency,
  RepoContractType,
  CDSContractType,
  TBillContractType,
  RFQRequest,
  RFQResponse,
  InterBankDeal,
  UnderlyingAsset,
  PnLDistributionType,
  PerInstrumentPnLDistribution,
} from 'state';
import * as constants from './constants';
import * as types from './types';
import { StoreState } from '../storeState';
import { cashExchange } from './utils';
import { useSelector } from 'react-redux';
import { ONGOING_TBILL_CONTRACT } from './auctions/types';
import { loan_tenors } from './client_money/types';
import { filterPlayerMarketInstruments } from './sessions/afsBondMarketVisibility';

export const appLoaded = (state: StoreState) => !!state.app;

export const paginatedSessions = (state: StoreState) =>
  state.app.paginated_sessions;
export const sessions = (state: StoreState) =>
  Object.values(state.app.sessions.items);
export const replaySessionDefaults = (state: StoreState) =>
  state.app.replay_session_defaults.item;
export const createSessionWaiting = (state: StoreState) =>
  state.app.create_session_request.waiting;
export const createKlassWaiting = (state: StoreState) =>
  state.app.create_klass_waiting;
export const createSessionSucceeded = (state: StoreState) =>
  state.app.create_session_request.succeeded;
export const createSessionFailed = (state: StoreState) =>
  state.app.create_session_request.failed;
export const replaySessionDefaultsWaiting = (state: StoreState) =>
  state.app.replay_session_defaults.waiting;

// Development Plan (multi-tree: GET /api/devplan/plans + /plans/<code>/me)
export const devPlan = (state: StoreState) => state.app.devplan.item;
export const devPlanFetched = (state: StoreState) => state.app.devplan.fetched;
export const devPlanError = (state: StoreState) => state.app.devplan.error;
export const devPlans = (state: StoreState) => state.app.devplan.plans;
export const devPlansFetched = (state: StoreState) =>
  state.app.devplan.plansFetched;
export const devPlanItemCode = (state: StoreState) =>
  state.app.devplan.itemPlanCode;
export const devPlanLaunch = (state: StoreState) => state.app.devplan.launch;

// Admin multi-tree editor (state.app.devplanAdmin — separate from the trainee slice).
export const devAdminPlans = (state: StoreState) =>
  state.app.devplanAdmin.plans;
export const devAdminPlansFetched = (state: StoreState) =>
  state.app.devplanAdmin.plansFetched;
export const devAdminContent = (state: StoreState) =>
  state.app.devplanAdmin.content;
export const devAdminContentCode = (state: StoreState) =>
  state.app.devplanAdmin.contentCode;
export const devAdminContentFetched = (state: StoreState) =>
  state.app.devplanAdmin.contentFetched;
export const devAdminAssets = (state: StoreState) =>
  state.app.devplanAdmin.assets;
export const devAdminAssetsFetched = (state: StoreState) =>
  state.app.devplanAdmin.assetsFetched;
export const devAdminSaving = (state: StoreState) =>
  state.app.devplanAdmin.saving;
export const devAdminError = (state: StoreState) =>
  state.app.devplanAdmin.error;
export const devAdminPublishProblems = (state: StoreState) =>
  state.app.devplanAdmin.publishProblems;

// Mock node recommendations (demo feature) — admin-only.
export const devAdminRecommendations = (state: StoreState) =>
  state.app.devplanAdmin.recommendations;
export const devAdminRecommendationsCode = (state: StoreState) =>
  state.app.devplanAdmin.recommendationsCode;
export const devAdminRecTargets = (state: StoreState) =>
  state.app.devplanAdmin.recTargets;
export const devAdminRecTargetsFetched = (state: StoreState) =>
  state.app.devplanAdmin.recTargetsFetched;
export const devAdminLastRecGranted = (state: StoreState) =>
  state.app.devplanAdmin.lastRecGranted;

export const syntheticBondCharts = (state: StoreState) =>
  state.app.synthetic_bond_playground.bonds;
export const syntheticBondChartsFetched = (state: StoreState) =>
  state.app.synthetic_bond_playground.fetched;
export const syntheticBondChartsFetching = (state: StoreState) =>
  state.app.synthetic_bond_playground.fetching;
export const syntheticBondChartsError = (state: StoreState) =>
  state.app.synthetic_bond_playground.error;

export const syntheticBondGeneration = (state: StoreState) =>
  state.app.synthetic_bonds;
export const syntheticBondGenerationOptions = (state: StoreState) =>
  state.app.synthetic_bonds.options;
export const syntheticBondGenerationDraft = (state: StoreState) =>
  state.app.synthetic_bonds.draftSpec;
export const syntheticBondGenerationPreview = (state: StoreState) =>
  state.app.synthetic_bonds.preview;
export const activeSyntheticBondGenerationBatch = (state: StoreState) =>
  state.app.synthetic_bonds.activeBatch;

export const replaySessionDefaultsFetched = (state: StoreState) =>
  state.app.replay_session_defaults.fetched;
export const replaySessionSourceId = (state: StoreState) =>
  state.app.replay_session_defaults.source_session_id;
export const replayTargetKlasses = (state: StoreState) =>
  state.app.replay_target_klasses.items;
export const replayTargetKlassesWaiting = (state: StoreState) =>
  state.app.replay_target_klasses.waiting;
export const sessionById = (id: number) => (state: StoreState) =>
  state.app.sessions.items[id];
export const sessionInitialAUM = (id: number) => (state: StoreState) => {
  const { risk_approach, starting_cash_amount, risk_weighted_limit } =
    sessionById(id)(state);
  return risk_approach === CASH_APPROACH
    ? starting_cash_amount
    : risk_weighted_limit;
};
export const selectedPerson = (state: StoreState) =>
  state.app.persons?.items[state.auth.item.selected_person || 0];
export const activeSession = (state: StoreState) => {
  return state.app.sessions.items[state.app.active_session];
};
export const analysisWaiting = (state: StoreState) => {
  return (
    state.app.sessions.items[state.app.active_session]?.analysis?.waiting ??
    false
  );
};
export const pnlDistributionWaiting = (state: StoreState) => {
  return (
    state.app.sessions.items[state.app.active_session]?.pnl_distributions
      ?.waiting ?? false
  );
};
export const analysis = (state: StoreState) => {
  return (
    state.app.sessions.items[state.app.active_session]?.analysis?.data ?? []
  );
};
export const leaderbordScores = (state: StoreState) =>
  state.app.sessions.items[state.app.active_session].leaderboard_scores;
export const pnlDistributions = (state: StoreState) =>
  state.app.sessions.items[state.app.active_session]?.pnl_distributions?.data ??
  [];
export const turnoverItems = (state: StoreState) =>
  state.app.sessions.items[state.app.active_session]?.turnover?.data ?? [];
export const analysisItemsSelector = (state: StoreState) =>
  state.app.sessions.items[state.app.active_session]?.analysis?.data ?? [];
// Anonymised cohort scatter points for the per-player (trainee) studio. Empty
// array when not fetched or suppressed (tiny cohort) — the tile hides on empty.
export const riskScatterPoints = (state: StoreState) =>
  state.app.sessions.items[state.app.active_session]?.risk_scatter?.data
    ?.points ?? [];

export const pnlDistributionByPlayerId =
  (playerId: number) => (state: StoreState) => {
    return state.app.sessions.items[
      state.app.active_session
    ].pnl_distributions.data.find(item => item.player_id === playerId);
  };

export const concentrationByPlayerId =
  (playerId: number) => (state: StoreState) => {
    return (
      state.app.sessions.items[
        state.app.active_session
      ].concentrations.data.find(item => item.player_id === playerId)
        ?.player_concentrations ?? []
    );
  };

export const badges = (state: StoreState) => state.app.badges.items;
export const badgesList = (state: StoreState) =>
  Object.values(state.app.badges.items);
export const orders = (state: StoreState) =>
  Object.values(state.app.orders.items);
export const ordersFetching = (state: StoreState) => state.app.orders.fetching;

export const failedExecutions = (state: StoreState) => {
  return Object.values(state.app.orders.items)
    .filter(order => order.status === CREATED)
    .reduce((acc, order) => {
      if (
        order.execution_errors === undefined ||
        order.execution_errors.length === 0
      )
        return acc;
      const error = order.execution_errors[order.execution_errors.length - 1];

      if (error.message !== 'Market is not open!') {
        acc.push(error);
      }

      return acc;
    }, [] as OrderExecutionError[]);
};

export const lastOrder = (state: StoreState) => state.app.orders.last;
export const ordersListByPlayerId =
  (playerId: number) => (state: StoreState) => {
    const player = state.app.players.items[playerId];
    const instruments = Object.values(player.financial_instruments).map(
      item => item.id,
    );
    return orders(state).filter(order =>
      instruments.includes(order.financial_instrument),
    );
  };
export const fwdContractsFetching =
  (player_id: number | undefined) => (state: StoreState) => {
    if (player_id) {
      return state.app.players.items[player_id].forward_contracts.fetching;
    }
    return state.app.player.item.forward_contracts.fetching;
  };

export const fwdContracts = (state: StoreState) => {
  return Object.values(state.app.player.item.forward_contracts.items);
};

export const fwdContractById =
  (id: number | undefined) => (state: StoreState) => {
    if (id) {
      return state.app.player.item.forward_contracts.items[id];
    } else {
      return undefined;
    }
  };

export const fwdContractsIndexed =
  (player_id: number | undefined) => (state: StoreState) => {
    if (player_id) {
      return state.app.players.items[player_id].forward_contracts.items;
    }
    return state.app.player.item.forward_contracts.items;
  };
export const exchangeHistory = (state: StoreState) =>
  state.app.player.item.exchange_history;

export const settledRepoContracts = (state: StoreState) => {
  return Object.values(state.app.player.item.repo_contracts.items).filter(
    contract => contract.settled,
  );
};

// export const CDSContracts = (state: StoreState) => {
//   const cds_fins_with_contracts = Object.values(
//     state.app.player.item.financial_instruments
//   ).filter(fin => fin.cds_contracts !== null);
//   const CDSContracts: CDSContractType[] = [];
//   cds_fins_with_contracts.forEach(fin =>
//     fin.cds_contracts.forEach(contract => CDSContracts.push(contract))
//   );
//   return CDSContracts;
// };

export const CDSContracts = (state: StoreState) => {
  return Object.values(state.app.player.item.cds_contracts.items);
};

export const fetchedForwardsByPlayerId =
  (playerId: number) => (state: StoreState) => {
    return state.app.players.items[playerId].forward_contracts.fetched;
  };
export const fetchingForwardsByPlayerId =
  (playerId: number) => (state: StoreState) => {
    return state.app.players.items[playerId].forward_contracts.fetching;
  };

export const TBillsClosedContracts = (state: StoreState) => {
  const tbill_fins_with_contracts = Object.values(
    state.app.player.item.financial_instruments,
  ).filter(fin => fin.tbill_contracts !== null);
  const TBillContracts: TBillContractType[] = [];
  tbill_fins_with_contracts.forEach(fin =>
    fin.tbill_contracts.forEach(contract => {
      if (contract.status !== ONGOING_TBILL_CONTRACT) {
        TBillContracts.push(contract);
      }
    }),
  );
  return TBillContracts;
};

export const depositsHistory = (state: StoreState) =>
  state.app.player.item.deposits_history;

export const underlyingAssetsApi = (state: StoreState) =>
  state.app.underlying_assets;
export const lsegEodQuota = (state: StoreState) => state.app.lseg_eod_quota;
export const intradayFetchStatus = (state: StoreState) =>
  state.app.intraday_fetch_status;
export const eodFetchStatus = (state: StoreState) =>
  state.app.eod_fetch_status;
export const underlyingAssets = (state: StoreState) =>
  state.app.underlying_assets.items;
export const udnerlyingAssetsOfActiveSession = (state: StoreState) =>
  state.app.sessions.items[state.app.active_session].underlying_assets;
export const underlyingAssetsList = (state: StoreState) =>
  Object.values(state.app.underlying_assets.items);
export const underlyingDepositsList = (state: StoreState) =>
  Object.values(state.app.underlying_deposits.items);
export const underlyingAssetById = (id: number) => (state: StoreState) =>
  state.app.underlying_assets.items[id];

export const equityList = (state: StoreState) =>
  Object.values(state.app.equities.items);
export const equitiesApi = (state: StoreState) => state.app.equities;
export const equities = (state: StoreState) => state.app.equities.items;

export const dividends = (state: StoreState) => state.app.dividends.items;
export const dividendsApi = (state: StoreState) => state.app.dividends;

export const currencies = (state: StoreState) => state.app.currencies.items;
export const currenciesList = (state: StoreState) =>
  Object.values(state.app.currencies.items);
export const fxCrossList = (state: StoreState) => {
  return Object.values(state.app.fx_cross.items);
};

export const macroBackgrounds = (state: StoreState) =>
  state.app.macro_backgrounds.items;
export const macroBackgroundsWaiting = (state: StoreState) =>
  state.app.macro_backgrounds.waiting;

export const presets = (state: StoreState) => state.app.presets.items;
export const presetsWaiting = (state: StoreState) => state.app.presets.waiting;
export const presetTrainerAvailability = (state: StoreState) =>
  state.app.presets.trainerAvailability;
export const presetTrainerAvailabilityWaiting = (state: StoreState) =>
  state.app.presets.availabilityWaiting;
export const presetAssetBeingMadeVisible = (state: StoreState) =>
  state.app.presets.unhidingAssetId;

export const initialPresets = (state: StoreState) =>
  state.app.initial_presets.items;
export const presetsList = (state: StoreState) =>
  Object.values(state.app.presets.items);

export const blueprints = (state: StoreState) => state.app.blueprints.items;
export const blueprintsList = (state: StoreState) =>
  Object.values(state.app.blueprints.items);
export const blueprintsWaiting = (state: StoreState) =>
  state.app.blueprints.waiting;
export const blueprintsError = (state: StoreState) =>
  state.app.blueprints.error;

export const indexFunds = (state: StoreState) => state.app.index_funds.items;
export const indexFundsList = (state: StoreState) =>
  Object.values(state.app.index_funds.items);

export const etfDetails = (state: StoreState) =>
  Object.values(state.app.etf_details.items);

export const detailsFetching = (state: StoreState) =>
  state.app.etf_details.fetching;

export const financialInstruments = (state: StoreState) =>
  state.app.player.item.financial_instruments;
export const financialInstrumentsList = (state: StoreState) =>
  Object.values(state.app.player.item.financial_instruments);
export const playerMarketFinancialInstrumentsList = (state: StoreState) => {
  const instruments = financialInstrumentsList(state);
  const session = activeSession(state);
  return filterPlayerMarketInstruments(
    instruments,
    session?.afs_bond_switchover_enabled ?? false,
    session?.afs_bond_market_replacements ?? [],
  );
};
export const financialInstrumentsListByPlayerId =
  (playerId: number) => (state: StoreState) =>
    Object.values(state.app.players.items[playerId].financial_instruments);

export const wallets = (state: StoreState) =>
  Object.values(state.app.player.item.wallets);
export const walletById = (id: number) => (state: StoreState) => {
  return Object.values(state.app.player.item.wallets).find(
    wallet => wallet.id === id,
  );
};
export const walletByCurrency =
  (ccy: types.Currency) => (state: StoreState) => {
    return Object.values(state.app.player.item.wallets).find(
      wallet => wallet.currency === ccy.id,
    );
  };
export const klasses = (state: StoreState) => state.app.klasses.items;
export const klassesWaiting = (state: StoreState) => state.app.klasses.waiting;
export const klassesList = (state: StoreState) =>
  Object.values(state.app.klasses.items);

export const klassById = (id: number) => (state: StoreState) =>
  state.app.klasses.items[id];

// Per-user gate for the trainee Development Plan + Games tabs. Read live off the
// klasses payload the dashboard already fetches (the logged-in trainee is one of
// klass.trainees), so an admin toggle takes effect on the next load with no extra
// request. Defaults to false (tabs hidden) whenever klasses aren't loaded yet or the
// flag is absent — hidden is the safe direction, matching pre-existing behavior.
export const canSeeDevplan = (state: StoreState): boolean => {
  const userId = state.auth.item.userId;
  if (userId == null) return false;
  const klassMap = state.app.klasses?.items;
  if (!klassMap) return false;
  for (const klass of Object.values(klassMap)) {
    const me = klass.trainees?.find(t => Number(t.id) === Number(userId));
    if (me) return me.can_see_devplan ?? false;
  }
  return false;
};

export const player = (state: StoreState) => state.app.player.item;

export const hasActiveRequest = (state: StoreState) => {
  const requests = state.app.player.item.sent_rfq_requests;
  let hasActiveRequest = false;
  requests.forEach(request => {
    if (request.active) {
      hasActiveRequest = true;
    }
  });
  return hasActiveRequest;
};

export const hasActiveCBRequest = (state: StoreState) => {
  const requests = state.app.player.item.cb_requests.filter(req => req.pending);
  if (requests.length > 0) {
    return true;
  }
  return false;
};

export const activeRFQRequest = (state: StoreState): RFQRequest | undefined => {
  const requests = state.app.player.item.sent_rfq_requests;
  let activeRequest: RFQRequest | undefined = undefined;
  // console.log('requests', requests);
  requests.forEach(request => {
    // console.log(`request ${request.id} active: `, request.active);
    if (request.active) {
      activeRequest = request;
    }
  });
  return activeRequest;
};

export const playerSentRFQRequests = (state: StoreState) => {
  return state.app.player.item.sent_rfq_requests;
};

export const responsesForActiveRequests = (
  state: StoreState,
): RFQResponse[] => {
  const responses = state.app.player.item.rfq_responses;
  const activeResponses: RFQResponse[] = [];
  responses.forEach(response => {
    if (response.request_active) {
      activeResponses.push(response);
    }
  });
  return activeResponses;
};

export const lockedInResponsesToYourRFQ = (
  state: StoreState,
): RFQResponse[] => {
  const yourActiveRFQ = activeRFQRequest(state);
  const lockedInResponses: RFQResponse[] = [];
  const rfqResponses = yourActiveRFQ?.rfq_responses;
  if (rfqResponses && rfqResponses.length > 0) {
    rfqResponses.forEach(rfqResponse => {
      if (rfqResponse.locked_in) {
        lockedInResponses.push(rfqResponse);
      }
    });
  }
  return lockedInResponses;
};

export const pendingCentralBankResponses = (state: StoreState) => {
  return state.app.player.item.cb_requests.filter(req => req.pending);
};

export const activeCentralBankDepos = (state: StoreState) => {
  return state.app.player.item.cb_requests.filter(req => req.active);
};

export const pastCentralBankDepos = (state: StoreState) => {
  return state.app.player.item.cb_requests.filter(
    req => !req.active && !req.pending && req.deal_taken,
  );
};

export const currentPeriodInterbankDeals = (state: StoreState) => {
  const interbankDeals = state.app.player.item.interbank_deals;
  const activeDeals: InterBankDeal[] = [];
  interbankDeals.forEach(deal => {
    if (deal.active_interbank_deal) {
      activeDeals.push(deal);
    }
  });
  return activeDeals;
};

export const pastPeriodInterbankDeals = (state: StoreState) => {
  const interbankDeals = state.app.player.item.interbank_deals;
  const pastDeals: InterBankDeal[] = [];
  interbankDeals.forEach(deal => {
    if (!deal.active_interbank_deal) {
      pastDeals.push(deal);
    }
  });
  return pastDeals;
};

export const metrics = (state: StoreState) => state.app.player.item.metrics;

export const assetByTicker = (ticker: string) => (state: StoreState) =>
  Object.values(state.app.underlying_assets.items).find(
    item => item.ticker === ticker,
  );

export const currencyById = (id: number) => (state: StoreState) =>
  state.app.currencies.items[id];

export const playerBadges = (state: StoreState) => state.app.player.item.badges;
export const playerById = (id: number) => (state: StoreState) =>
  state.app.players.items[id];

export const perAssetClassPnLByPlayerId =
  (playerId: number, assetClassNumber: number) =>
  (state: StoreState): PerInstrumentPnLDistribution[] => {
    const session = state.app.sessions.items[state.app.active_session];

    const playerDist = session?.pnl_distributions?.data?.find(
      item => item.player_id === playerId,
    );
    if (!playerDist) return [];

    return (
      playerDist.distributions?.find(
        item => item.asset_class === assetClassNumber,
      )?.per_instrument_distributions ?? []
    );
  };
export const individualReport = (player_id: number) => (state: StoreState) => {
  return state.app.individualReports[player_id];
};

export const financialInstrumentById =
  (id: number, player_id?: number) => (state: StoreState) =>
    player_id
      ? state.app.players.items[player_id].financial_instruments[id]
      : state.app.player.item.financial_instruments[id];

export const financialInstrumentById2 =
  (id: number, player_id?: number) => (state: StoreState) => {
    if (id !== undefined) {
      return player_id
        ? state.app.players.items[player_id].financial_instruments[id]
        : state.app.player.item.financial_instruments[id];
    } else {
      return undefined;
    }
  };

export const OdaOfForwardContract = (id: number) => (state: StoreState) => {
  const contract = state.app.player.item.forward_contracts.items[id];
  const oda_id = contract.oda;
  if (oda_id !== null) {
    return state.app.player.item.forward_oda.items[oda_id];
  }
};

export const forwardContractsOfInstrumentList =
  (id: number, player_id?: number) => (state: StoreState) => {
    if (player_id) {
      // called from trainer view
      const contract_ids =
        state.app.players.items[player_id].financial_instruments[id]
          .forward_contracts;
      const contractObjectsOfInstrumentList: ForwardContract[] = Object.values(
        state.app.players.items[player_id].forward_contracts.items,
      ).filter((contract: ForwardContract) =>
        contract_ids.includes(contract.id),
      );
      return contractObjectsOfInstrumentList;
    } else {
      // called from player view
      const contract_ids =
        state.app.player.item.financial_instruments[id].forward_contracts;
      const contractObjectsOfInstrumentList: ForwardContract[] = Object.values(
        state.app.player.item.forward_contracts.items,
      ).filter((contract: ForwardContract) =>
        contract_ids.includes(contract.id),
      );
      return contractObjectsOfInstrumentList;
    }
  };

export const cdsContractsFetching = (state: StoreState) => {
  return state.app.player.item.cds_contracts.fetching;
};
export const repoContractsFetching = (state: StoreState) => {
  return state.app.player.item.repo_contracts.fetching;
};

export const CDSContractsOfInstrument = (id: number) => (state: StoreState) => {
  const contractIds =
    state.app.player.item.financial_instruments[id].cds_contracts ?? [];
  const allContracts = state.app.player.item.cds_contracts.items;
  return contractIds.map(cid => allContracts[cid]);
  // return state.app.player.item.financial_instruments[id].cds_contracts;
};

export const TBillContractsOfInstrument = (id: number) => (state: StoreState) =>
  state.app.player.item.financial_instruments[id].tbill_contracts;

export const RepoContractsOfInstrument =
  (id: number) => (state: StoreState) => {
    const contractIds =
      state.app.player.item.financial_instruments[id].repo_contracts ?? [];
    const allContracts = state.app.player.item.repo_contracts.items;
    return contractIds.map(cid => allContracts[cid]);
    // return state.app.player.item.financial_instruments[id].repo_contracts;
  };

export const financialInstrumentByUnderlingAsset =
  (id: number) => (state: StoreState) => {
    const instruments = state.app.player.item.financial_instruments;
    if (!instruments) return;

    return Object.values(instruments).find(
      item => item.underlying_asset === id,
    );
  };

export const underlyingAssetOfInstrument =
  (instrument: types.FinancialInstrument) => (state: StoreState) =>
    state.app.underlying_assets.items[instrument.underlying_asset];

export const underlyingAssetOfInstrument2 =
  (instrument: types.FinancialInstrument | undefined) =>
  (state: StoreState) => {
    if (instrument !== undefined) {
      return state.app.underlying_assets.items[instrument.underlying_asset];
    } else {
      return undefined;
    }
  };

export const odaOfInstrumentCreateSelector = (instrument_id: number) =>
  createSelector(
    [financialInstrumentById(instrument_id), state => state],
    (instrument, state) => {
      const oda = instrument.oda;
      if (!oda) return;

      const orders = state.app.orders.items;
      const { stop_loss, take_profit } = oda;

      return { stopLoss: orders[stop_loss], takeProfit: orders[take_profit] };
    },
  );
export const sessionHasHTMBonds = (state: StoreState) => {
  const underlyingAssets = Object.values(state.app.underlying_assets.items);
  const htmAssets = underlyingAssets.filter(
    asset => asset.resourcetype === 'HTMBond',
  );
  const hasHTMassets = htmAssets.length !== 0;
  return hasHTMassets;
};

export const sessionHasOptions = (state: StoreState) => {
  // This will truly filter session's assets only in player view.
  // Trainer's state.app.underlying_assets contains all platform assets.
  const underlyingAssets = Object.values(state.app.underlying_assets.items);
  const Options = underlyingAssets.filter(
    asset => asset.resourcetype === 'Option',
  );
  return Options.length !== 0;
};

export const sessionHasHTMassetsOverview = (state: StoreState) => {
  const session = state.app.sessions.items[state.app.active_session];
  const sessionAssets = session.underlying_assets;
  const htmAssets = sessionAssets.filter(asset_id => {
    const resourceType =
      state.app.underlying_assets.items[asset_id]?.resourcetype;
    if (resourceType === undefined) {
      return false;
    }

    return resourceType === 'HTMBond';
  });
  return htmAssets.length !== 0;
};

export const sessionHasAFSBonds = (state: StoreState) => {
  const underlyingAssets = Object.values(state.app.underlying_assets.items);
  const htmAssets = underlyingAssets.filter(
    asset => asset.resourcetype === 'AFSBond',
  );
  const hasAFSassets = htmAssets.length !== 0;
  return hasAFSassets;
};

export const sessionHasAFSassetsOverview = (state: StoreState) => {
  const session = state.app.sessions.items[state.app.active_session];
  const sessionAssets = session.underlying_assets;
  const htmAssets = sessionAssets.filter(asset_id => {
    const resourceType =
      state.app.underlying_assets.items[asset_id]?.resourcetype;
    if (resourceType === undefined) {
      return false;
    }
    return resourceType === 'AFSBond';
  });
  return htmAssets.length !== 0;
};

export const sessionHasRepos = (state: StoreState) => {
  // This will truly filter session's assets only in player view.
  // Trainer's state.app.underlying_assets contains all platform assets.
  const underlyingAssets = Object.values(state.app.underlying_assets.items);
  const repoAssets = underlyingAssets.filter(
    asset => asset.resourcetype === 'Repo',
  );
  const hasRepos = repoAssets.length !== 0;
  return hasRepos;
};

export const sessionHasCDS = (state: StoreState) => {
  const underlyingAssets = Object.values(state.app.underlying_assets.items);
  const CDSAssets = underlyingAssets.filter(
    asset => asset.resourcetype === 'CDS',
  );
  const hasCDS = CDSAssets.length !== 0;
  return hasCDS;
};
// TODO: these could be one function
export const sessionHasFWD = (state: StoreState) => {
  const underlyingAssets = Object.values(state.app.underlying_assets.items);
  const FWDAssets = underlyingAssets.filter(
    asset => asset.resourcetype === 'FxForwards',
  );
  const hasFWD = FWDAssets.length !== 0;
  return hasFWD;
};
export const sessionHasTBills = (state: StoreState) => {
  const underlyingAssets = Object.values(state.app.underlying_assets.items);
  const TBillsAssets = underlyingAssets.filter(
    asset => asset.resourcetype === 'TBill',
  );
  const hasTBills = TBillsAssets.length !== 0;
  return hasTBills;
};

export const bondsValueDistribution = (state: StoreState) => {
  const assetList = Object.values(state.app.underlying_assets.items);
  const currencyList = Object.values(state.app.currencies.items);
  const indexedCurrencies = currencies(state);
  const underlyingAssets = state.app.underlying_assets.items;
  const bonds = Object.values(state.app.player.item.financial_instruments)
    .filter(item =>
      underlyingAssets[item.underlying_asset].resourcetype.endsWith('Bond'),
    )
    .filter(item => item.amount);
  const session = activeSession(state);
  const reportingCurrency = indexedCurrencies[session.reporting_currency];

  const intervals = [0, 2, 5, 10, 20]; // 20+
  const data = intervals.reduce(
    (acc, value) => {
      acc[value] = [];
      return acc;
    },
    {} as { [key: number]: number[] },
  );

  bonds.forEach(item => {
    const { duration } = underlyingAssets[item.underlying_asset];
    const underlying_asset = underlyingAssets[item.underlying_asset];

    const reportingValue = utils.reportingValue(
      item,
      indexedCurrencies[underlying_asset.base_currency],
      underlyingAssets[item.underlying_asset].resourcetype,
      assetList,
      currencyList,
      reportingCurrency,
    );

    let interval = -1;
    for (const i of intervals) {
      if (duration < i) {
        break;
      }
      interval = i;
    }

    if (interval === -1) {
      return;
    }

    data[interval].push(reportingValue);
  });

  const categories = intervals.map((interval, index) => {
    return index !== intervals.length - 1
      ? `${interval} - ${intervals[index + 1]}`
      : `${interval}+`;
  });

  return {
    series: [
      {
        data: intervals.map(interval => {
          return data[interval].reduce((s, n) => s + n, 0);
        }),
        name: 'Bonds',
      },
    ],
    categories,
  };
};

export const cashTacticalAssetAllocation = (state: StoreState) =>
  state.app.player.item.tactical_asset_allocations.find(
    item => item.asset_class === constants.CASH,
  );

const computeDuration = (
  state: StoreState,
  filter: (item: types.FinancialInstrument) => boolean,
) => {
  const underlyingAssets = state.app.underlying_assets.items;
  const values = Object.values(state.app.player.item.financial_instruments)
    .filter(item =>
      underlyingAssets[item.underlying_asset].resourcetype.endsWith('Bond'),
    )
    .filter(item => item.amount)
    .filter(filter)
    .reduce(
      (acc, item) => {
        const underlyingAsset = underlyingAssets[item.underlying_asset];
        const marketValue = (Math.abs(item.amount) * underlyingAsset.bid) / 100;
        return [
          acc[0] + underlyingAsset.duration * marketValue,
          acc[1] + marketValue,
        ];
      },
      [0, 0],
    );

  if (!values[1]) return 0;
  return values[0] / values[1];
};

export const portfolioDuration = (state: StoreState) => {
  return computeDuration(state, () => true);
};

export const portfolioDurationShort = (state: StoreState) => {
  return computeDuration(state, item => item.amount < 0);
};

export const portfolioDurationLong = (state: StoreState) => {
  return computeDuration(state, item => item.amount > 0);
};

export const portfolioDv01 = (state: StoreState) => {
  const underlyingAssets = state.app.underlying_assets.items;
  return Object.values(state.app.player.item.financial_instruments)
    .filter(item =>
      underlyingAssets[item.underlying_asset].resourcetype.endsWith('Bond'),
    )
    .filter(item => item.amount)
    .reduce(
      (a, b) =>
        a + (-b.amount / 10000) * underlyingAssets[b.underlying_asset].bpv,
      0,
    );
};

export const playersList = (state: StoreState) =>
  Object.values(state.app.players.items);

export const prompts = (state: StoreState) => state.app.prompts.items || {};
export const groupPrompts = (state: StoreState) =>
  state.app.group_prompts.items || {};
export const llmResponses = (state: StoreState) =>
  state.app.llm_responses.items || {};
export const llmGroupResponses = (state: StoreState) =>
  state.app.llm_group_responses.items || {};
export const promptsWaiting = (state: StoreState) =>
  state.app.prompts.waiting || false;
export const groupPromptsWaiting = (state: StoreState) =>
  state.app.group_prompts.waiting || false;
export const llmResponsesWaiting = (state: StoreState) =>
  state.app.llm_responses.waiting || false;
export const llmGroupResponsesWaiting = (state: StoreState) =>
  state.app.llm_group_responses.waiting || false;
export const biasReports = (state: StoreState) =>
  state.app.bias_reports.items || {};
export const biasReportsWaiting = (state: StoreState) =>
  state.app.bias_reports.waiting || false;
export const marketRegimesWaiting = (state: StoreState) =>
  state.app.market_regimes.waiting || false;
// Batch generation in flight (admin Generate-button lock). True from the
// request until the server's *_generation_finished websocket bracket clears it.
export const biasGenerating = (state: StoreState) =>
  state.app.bias_reports.generating || false;
export const reportGenerating = (state: StoreState) =>
  state.app.llm_responses.generating || false;
export const regimeOverlayFetching = (state: StoreState) =>
  state.app.market_regimes.fetching || false;
export const regimeOverlay = (state: StoreState) =>
  state.app.market_regimes.overlay || null;
export const adminAISessions = (state: StoreState) =>
  Object.values(state.app.admin_ai_sessions.items || {});
export const adminAISessionsWaiting = (state: StoreState) =>
  state.app.admin_ai_sessions.waiting || false;
export const adminAIPlayers = (state: StoreState) =>
  Object.values(state.app.admin_ai_players.items || {});
export const adminAIPlayersWaiting = (state: StoreState) =>
  state.app.admin_ai_players.waiting || false;
export const adminAISessionContext = (state: StoreState) =>
  state.app.admin_ai_session_context.item;
export const adminAISessionContextWaiting = (state: StoreState) =>
  state.app.admin_ai_session_context.waiting || false;
export const adminAISessionContextError = (state: StoreState) =>
  state.app.admin_ai_session_context.error;
export const activePlayers = (state: StoreState) =>
  playersList(state)
    .filter(player => player.last_portfolio_return)
    .sort((a, b) => (a.name < b.name ? -1 : 1));
export const playersListBySessionId = (id: number) => (state: StoreState) =>
  Object.values(state.app.players.items).filter(item => item.session == id);

export const playersAllocations = (state: StoreState) =>
  Object.values(state.app.players.items)
    .filter(
      player =>
        !player.tactical_asset_allocations?.every(
          allocation => allocation.percent === 0,
        ),
    )
    .map(player => {
      const allocations = player.tactical_asset_allocations?.reduce(
        (acc, p) => {
          const group = GROUP_OF_ASSET[p.asset_class];
          const prev = acc[group] ?? 0;
          return { ...acc, [group]: prev + p.percent };
        },
        {} as { [key: number]: number },
      );

      return {
        ...allocations,
        ...player,
      };
    });

export const persons = (state: StoreState) => state.app.persons?.items;
export const personsList = (state: StoreState) =>
  Object.values(state.app.persons?.items || {}).filter(
    p => !p.name.toLowerCase().includes('admin'),
  );
export const personsListAdmin = (state: StoreState) =>
  Object.values(state.app.persons?.items || {});

export const waiting = (state: any) => state.app.waiting;
export const waitingSessions = (state: any) => state.app.sessions.waiting;
export const error = (state: any) => state.app.error;
export const lastAction = (state: any) => state.app.lastAction;

export const newsFetching = (state: StoreState) => state.app.news.fetching;
export const newsList = (state: StoreState) =>
  Object.values(state.app.news.items).sort((a, b) =>
    a.timestamp < b.timestamp ? 1 : -1,
  );
export const marketsNewsList = (state: StoreState) => {
  const timestampOrderedNews = newsList(state);
  const delivery = state.app.news.historical_delivery;
  if (!delivery || delivery.session_id !== state.app.active_session) {
    return timestampOrderedNews;
  }

  const deliveredIds = new Set<number>();
  const deliveredNews = delivery.delivery_order.reduce<types.News[]>(
    (items, newsId) => {
      const item = state.app.news.items[newsId];
      if (!item || deliveredIds.has(newsId)) return items;
      deliveredIds.add(newsId);
      items.push(item);
      return items;
    },
    [],
  );
  return [
    ...deliveredNews,
    ...timestampOrderedNews.filter(news => !deliveredIds.has(news.id)),
  ];
};
export const currentPeriodNewsIds = (state: StoreState) => {
  const delivery = state.app.news.historical_delivery;
  if (!delivery || delivery.session_id !== state.app.active_session) {
    return undefined;
  }
  return delivery.current_period_news_ids;
};
export const newsById = (id: number) => (state: StoreState) =>
  state.app.news.items[id];
export const lastNews = (state: StoreState) => state.app.news.last;
export const lastNewsSource = (state: StoreState) =>
  state.app.sessions.items[state.app.active_session]?.last_news_source ||
  undefined;

export const playersCount = (state: StoreState) => playersList(state).length;
export const onlinePlayersCount = (state: StoreState) =>
  playersList(state).reduce((acc, pl) => acc + +pl.online, 0);

export const initialPortfolioStatus = (state: StoreState) =>
  state.app.initialPortfolioStatus;

export const remainingReportingHedge = (state: StoreState) =>
  state.app.player.item.remaining_reporting_absolute_hedge_amount;

export const remainingReportingAbsoluteBondLeverage = (state: StoreState) =>
  state.app.player.item.remaining_reporting_absolute_bond_leverage;

export const remainingReportingAbsoluteEquityLeverage = (state: StoreState) =>
  state.app.player.item.remaining_reporting_absolute_equity_leverage;

export const remainingReportingAbsoluteETFEquitiesLeverage = (
  state: StoreState,
) => state.app.player.item.remaining_reporting_absolute_etf_equities_leverage;

export const remainingReportingAbsoluteETFCommoditiesLeverage = (
  state: StoreState,
) =>
  state.app.player.item.remaining_reporting_absolute_etf_commodities_leverage;

export const remainingReportingAbsoluteFuturesLeverage = (state: StoreState) =>
  state.app.player.item.remaining_reporting_absolute_futures_leverage;

export const hedgeAmountOfInstrument =
  (instrument: types.FinancialInstrument) => (state: StoreState) => {
    const orders = Object.values(state.app.orders.items).filter(
      od => od.hedge_for === instrument.id,
    );

    let result = 0;
    orders.forEach(order => {
      const sign = order.transaction === BUY ? 1 : -1;

      result += order.quantity * sign;
    });

    return result;
  };

export const playableInstrumentTypes = (state: StoreState) => {
  const instruments = Object.values(
    state.app.player.item.financial_instruments,
  );
  const assets = underlyingAssets(state);

  return instrumentTypes.filter(instrType => {
    const index = instruments.findIndex(instrument => {
      const resourcetype = assets[instrument.underlying_asset].resourcetype;
      return resourcetype === instrType.type;
    });

    return index !== -1;
  });
};
export const sessionAssets = () => (state: StoreState) => {
  const sessionAssetsIDs =
    state.app.sessions.items[state.app.active_session].underlying_assets;
  const assets = Object.values(state.app.underlying_assets.items);
  return assets.filter(asset => sessionAssetsIDs.includes(asset.id));
};
export const playableAssetClasses = () => (state: StoreState) => {
  const assets = sessionAssets()(state);
  const playableAssetClasses = Array.from(
    new Set(
      assets.map(asset => {
        // const asset = assets[instrument.underlying_asset];
        const resourceType = asset.resourcetype;
        const instrumentType = instrumentTypes.find(
          instrumentType => instrumentType.type === resourceType,
        )!;
        // console.log('instrumentType', instrumentType);
        return instrumentType.assetClass;
      }),
    ),
  );
  const session = state.app.sessions.items[state.app.active_session];
  if (session.risk_approach === CASH_APPROACH) {
    playableAssetClasses.push(0);
  }
  return playableAssetClasses;
};

// Same derivation as playableAssetClasses(), but over an explicit id list rather
// than the persisted session.underlying_assets — lets callers reflect a live,
// not-yet-saved selection (e.g. the settings rail's asset-class chips).
export const playableAssetClassesForIds =
  (assetIds: number[]) => (state: StoreState) => {
    const itemsById = state.app.underlying_assets.items;
    const classes = Array.from(
      new Set(
        assetIds
          .map(id => itemsById[id])
          .filter(Boolean)
          .map(
            asset =>
              instrumentTypes.find(t => t.type === asset.resourcetype)
                ?.assetClass,
          )
          .filter((assetClass): assetClass is number => assetClass != null),
      ),
    );
    const session = state.app.sessions.items[state.app.active_session];
    if (session.risk_approach === CASH_APPROACH) {
      classes.push(0);
    }
    return classes;
  };

export const pnlByAssetClass =
  (instruments: types.FinancialInstrument[]) => (state: StoreState) => {
    const assets = underlyingAssets(state);

    const pnlObject: {
      [assetClass: number]: {
        pnl: number;
      } & Omit<types.TacticalAssetAllocation, 'id'>;
    } = {};

    const { tactical_asset_allocations: allocations } = player(state);

    // filter playable asset classes (so you don't display equity and crypto allocs. if players does not have those fins.)
    const playableAssetClasses_ = playableAssetClasses()(state);
    const filteredAllocations = allocations.filter(allocation =>
      playableAssetClasses_.includes(allocation.asset_class),
    );

    filteredAllocations.forEach(({ id, ...allocation }) => {
      pnlObject[allocation.asset_class] = {
        pnl: 0,
        ...allocation,
      };
    });

    instruments.forEach(instrument => {
      const {
        underlying_asset,
        reporting_unrealized_pnl,
        reporting_realized_pnl,
      } = instrument;

      const resourcetype = assets[underlying_asset].resourcetype;
      const assetClass = instrumentTypes.find(
        t => t.type === resourcetype,
      )?.assetClass;
      if (assetClass === undefined) {
        return;
      }
      let pnl = reporting_unrealized_pnl + reporting_realized_pnl;
      if (resourcetype === 'HTMBond') {
        pnl = reporting_realized_pnl;
      }
      pnlObject[assetClass].pnl += pnl;
    });

    return Object.values(pnlObject);
  };

export const pnlByAssetGroup =
  (instruments: types.FinancialInstrument[]) => (state: StoreState) => {
    const assets = underlyingAssets(state);
    const { tactical_asset_allocations: allocations } = player(state);

    const pnlObject: {
      [assetGroup: number]: {
        pnl: number;
      } & Omit<types.TacticalAssetAllocation, 'id' | 'asset_class'>;
    } = {};

    // filter playable asset classes (so you don't display equity and crypto allocs. if players does not have those fins.)
    const playableAssetClasses_ = playableAssetClasses()(state);
    const filteredAllocations = allocations.filter(allocation =>
      playableAssetClasses_.includes(allocation.asset_class),
    );

    filteredAllocations.forEach(allocation => {
      const assetGroup = GROUP_OF_ASSET[allocation.asset_class];
      const value = pnlObject[assetGroup] ?? {
        pnl: 0,
        long: 0,
        short: 0,
        total: 0,
        percent: 0,
        percent_long: 0,
        percent_short: 0,
      };

      pnlObject[assetGroup] = {
        pnl: 0,
        long: value.long + allocation.long,
        short: value.short + allocation.short,
        total: value.total + allocation.total,
        percent: value.percent + allocation.percent,
        percent_long: value.percent_long + allocation.percent_long,
        percent_short: value.percent_short + allocation.percent_short,
      };
    });

    instruments.forEach(instrument => {
      const {
        underlying_asset,
        reporting_unrealized_pnl,
        reporting_realized_pnl,
      } = instrument;

      const resourcetype = assets[underlying_asset].resourcetype;
      const assetClass = instrumentTypes.find(
        t => t.type === resourcetype,
      )?.assetClass;

      if (assetClass === undefined) {
        return;
      }
      const assetGroup = GROUP_OF_ASSET[assetClass];
      if (assetGroup == undefined) {
        return;
      }

      const pnl = reporting_unrealized_pnl + reporting_realized_pnl;
      // A held instrument can belong to a group with no allocation row (e.g.
      // simplified options): seed a zero bucket instead of crashing the page.
      const bucket = (pnlObject[assetGroup] ??= {
        pnl: 0,
        long: 0,
        short: 0,
        total: 0,
        percent: 0,
        percent_long: 0,
        percent_short: 0,
      });
      bucket.pnl += pnl;
    });

    return Object.entries(pnlObject).map(entry => {
      const assetGroup = entry[0];
      const data = entry[1];
      return { assetGroup: Number(assetGroup), ...data };
    });
  };

export const HTMDurationBreakdown = () => (state: StoreState) => {
  const instruments = Object.values(
    state.app.player.item.financial_instruments,
  );
  const htmInstruments = instruments.filter(instrument => {
    const assetId = instrument.underlying_asset;
    const asset = state.app.underlying_assets.items[assetId];
    return asset.resourcetype === 'HTMBond';
  });
  const series: { x: string; y: number }[] = [];

  // these are the breakdown intervals: 0-3, 4-7, 8-15, 16-30, 31+
  for (let i = 1; i <= 5; i++) {
    let lower = 0;
    let upper = 0;
    if (i !== 5) {
      lower = Number(DURATION_INTERVALS_NAME[i].split('-')[0]);
      upper = Number(DURATION_INTERVALS_NAME[i].split('-')[1]);
    } else {
      lower = 31;
      upper = 1000;
    }

    let amountSum = 0;
    let unweightedSum = 0;
    htmInstruments.forEach(instrument => {
      if (
        instrument.htm_afs_duration < lower ||
        instrument.htm_afs_duration >= upper
      ) {
        return;
      }
      amountSum += instrument.amount;
      unweightedSum += instrument.amount * instrument.htm_afs_duration;
    });
    let weightedDuration = 0;
    if (amountSum !== 0) {
      weightedDuration = unweightedSum / amountSum;
    }

    const series_group = {
      x: DURATION_INTERVALS_NAME[i], // interval
      y: weightedDuration, // duration
    };
    series.push(series_group);
  }
  return series;
};

export const AFSDurationBreakdown = () => (state: StoreState) => {
  const instruments = Object.values(
    state.app.player.item.financial_instruments,
  );
  // const currencies = selectors.currencies;
  const currencies = state.app.currencies.items;
  const assets = state.app.underlying_assets.items;
  const htmInstruments = instruments.filter(instrument => {
    const assetId = instrument.underlying_asset;
    const asset = state.app.underlying_assets.items[assetId];
    return asset.resourcetype === 'AFSBond';
  });
  const series: { x: string; y: number }[] = [];

  // these are the breakdown intervals: 0-1, 1-2, 2-3, ... , 9-10, 10+
  for (let i = 1; i <= 5; i++) {
    // console.log('-- i: ', i);
    let lower = 0;
    let upper = 0;
    if (i !== 5) {
      lower = Number(DURATION_INTERVALS_NAME[i].split('-')[0]);
      upper = Number(DURATION_INTERVALS_NAME[i].split('-')[1]);
    } else {
      lower = 31;
      upper = 1000;
    }

    let amountSum = 0;
    let unweightedSum = 0;
    htmInstruments.forEach(instrument => {
      if (
        instrument.htm_afs_duration < lower ||
        instrument.htm_afs_duration > upper
      ) {
        return;
      }

      // console.log(instrument.htm_afs_duration);
      const reportingAmount2 = utils.cashExchange(
        assets[instrument.underlying_asset].base_currency,
        state.app.sessions.items[state.app.active_session].reporting_currency,
        instrument.amount,
        0,
        Object.values(state.app.underlying_assets.items),
        Object.values(state.app.currencies.items),
      );
      // amountSum += instrument.amount;
      amountSum += reportingAmount2;
      unweightedSum += instrument.amount * instrument.htm_afs_duration;
    });
    let weightedDuration = 0;
    if (amountSum !== 0) {
      weightedDuration = unweightedSum / amountSum;
    }

    const series_group = {
      x: DURATION_INTERVALS_NAME[i], // interval
      // y: weightedDuration, // duration
      y: amountSum, // holding
    };
    series.push(series_group);
  }
  return series;
};

export const portfolioPnl =
  (instruments?: types.FinancialInstrument[]) => (state: StoreState) => {
    instruments = instruments || financialInstrumentsList(state);

    let sum = 0;

    instruments.forEach(instrument => {
      const asset =
        state.app.underlying_assets.items[instrument.underlying_asset];
      if (asset.resourcetype === 'HTMBond') {
        sum += instrument.reporting_realized_pnl;
      } else {
        sum +=
          instrument.reporting_unrealized_pnl +
          instrument.reporting_realized_pnl;
      }
    });

    return sum;
  };

export const hasEquitiesGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  const hasEquity =
    sessionAssets_.filter(asset => asset.resourcetype === 'Equity').length > 0;
  const hasETFEquities =
    sessionAssets_.filter(asset => asset.resourcetype === 'ETFEquities')
      .length > 0;
  return hasEquity || hasETFEquities;
};

export const hasBondsGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  const hasGovtBonds =
    sessionAssets_.filter(asset => asset.resourcetype === 'GovtBond').length >
    0;
  const hasETFBonds =
    sessionAssets_.filter(asset => asset.resourcetype === 'ETFBonds').length >
    0;
  const hasCorporateBonds =
    sessionAssets_.filter(asset => asset.resourcetype === 'CorporateBond')
      .length > 0;
  return hasGovtBonds || hasETFBonds || hasCorporateBonds;
};

export const hasAlternativesGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  const hasMetals =
    sessionAssets_.filter(asset => asset.resourcetype === 'Metal').length > 0;
  const hasETFCommodities =
    sessionAssets_.filter(asset => asset.resourcetype === 'ETFCommodities')
      .length > 0;
  return hasMetals || hasETFCommodities;
};

export const hasCryptoGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  return (
    sessionAssets_.filter(asset => asset.resourcetype === 'Crypto').length > 0
  );
};

export const hasHTMGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  return (
    sessionAssets_.filter(asset => asset.resourcetype === 'HTMBond').length > 0
  );
};

export const hasAFSGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  return (
    sessionAssets_.filter(asset => asset.resourcetype === 'AFSBond').length > 0
  );
};

export const hasRepoGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  return (
    sessionAssets_.filter(asset => asset.resourcetype === 'Repo').length > 0
  );
};

export const hasCDSGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  return (
    sessionAssets_.filter(asset => asset.resourcetype === 'CDS').length > 0
  );
};

export const hasFuturesGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  return (
    sessionAssets_.filter(asset => asset.resourcetype === 'Futures').length > 0
  );
};

/** Detects only standard-flow class-19 options so legacy chain sessions remain isolated. */
export const hasSimplifiedOptionsGroup = () => (state: StoreState) => {
  const sessionAssets_ = sessionAssets()(state);
  return sessionAssets_.some(
    asset => asset.resourcetype === 'SimplifiedOption',
  );
};

type PnlByRegion = (instruments: types.FinancialInstrument[]) => (
  state: StoreState,
) => {
  region: keyof typeof REGIONS;
  pnl: number;
  percent: number;
  long: number;
  short: number;
}[];

export const pnlByRegion: PnlByRegion = instruments => state => {
  const assets = underlyingAssets(state);

  const playedRegions = new Set<number>();
  instruments.forEach(instrument => {
    const region = assets[instrument.underlying_asset]?.region;
    if (!region) {
      return;
    }
    playedRegions.add(region);
  });

  const pnlObject: { [key: string]: number } = {};
  playedRegions.forEach(region => (pnlObject[region] = 0));

  instruments.forEach(instrument => {
    const {
      underlying_asset,
      reporting_unrealized_pnl,
      reporting_realized_pnl,
    } = instrument;
    const asset = assets[underlying_asset];
    const region = assets[underlying_asset]?.region;
    if (!region) {
      return;
    }
    if (asset.resourcetype === 'HTMBond') {
      pnlObject[region] += reporting_realized_pnl;
    } else {
      pnlObject[region] += reporting_unrealized_pnl + reporting_realized_pnl;
    }
  });

  const { region_allocations: allocations } = player(state);

  return Object.entries(pnlObject).map(entry => {
    const region = Number(entry[0]) as keyof typeof REGIONS;
    const pnl = entry[1];

    let percent = 0;
    let long = 0;
    let short = 0;
    const regionAllocation = allocations.find(a => a.region === region);
    if (regionAllocation) {
      const allocations = regionAllocation.geography_allocations;
      percent = allocations.reduce((acc, a) => acc + a.percent, 0);
      long = allocations.reduce((acc, a) => acc + a.long, 0);
      short = allocations.reduce((acc, a) => acc + a.short, 0);
    }

    return {
      region,
      pnl,
      percent,
      long,
      short,
    };
  });
};

export const equityPnlByIndustry = (state: StoreState) => {
  const assets = underlyingAssets(state);
  const instruments = financialInstrumentsList(state);
  const indexedCurrencies = currencies(state);
  const session = activeSession(state);
  const reportingCurrency = indexedCurrencies[session.reporting_currency];

  const equities = instruments.filter(i => {
    const asset = assets[i.underlying_asset];
    if (!asset) {
      return false;
    }
    return ['Equity'].includes(asset.resourcetype);
  });

  const indexedIndustries: {
    [key: string]: {
      pnl: number;
      short: number;
      long: number;
      value: number;
      allocation: number;
    };
  } = {};

  const getDollarValue = (instrument: types.FinancialInstrument) => {
    const asset = assets[instrument.underlying_asset];
    const ccy = indexedCurrencies[asset.base_currency];

    return utils.reportingValue(
      instrument,
      ccy,
      asset.resourcetype,
      Object.values(assets),
      Object.values(indexedCurrencies),
      reportingCurrency,
    );
  };

  const initialAum = sessionInitialAUM(state.app.active_session)(state);

  equities.forEach(instrument => {
    const {
      underlying_asset,
      reporting_unrealized_pnl,
      reporting_realized_pnl,
    } = instrument;

    const industry = assets[underlying_asset]?.industry ?? 'Other';
    if (indexedIndustries[industry] === undefined) {
      indexedIndustries[industry] = {
        pnl: 0,
        long: 0,
        short: 0,
        value: 0,
        allocation: 0,
      };
    }

    const pnl = reporting_unrealized_pnl + reporting_realized_pnl;
    indexedIndustries[industry].pnl += pnl;

    const dollarValue = getDollarValue(instrument);
    const valueKey = dollarValue >= 0 ? 'long' : 'short';
    indexedIndustries[industry][valueKey] += dollarValue;
    indexedIndustries[industry].value += dollarValue;
  });

  const industryData = Object.entries(indexedIndustries)
    .map(([industry, { value, ...data }]) => {
      return {
        ...data,
        industry,
        value,
        allocation: value / initialAum,
      };
    })
    .filter(i => i.value !== 0 || i.pnl !== 0);

  if (industryData.length <= 10) {
    return industryData;
  }

  let other = undefined;
  const otherIndex = industryData.findIndex(i => i.industry === 'Other');
  if (otherIndex !== -1) {
    other = industryData.splice(otherIndex, 1)[0];
  }

  industryData.sort((a, b) => b.value - a.value);

  const topTen = industryData.splice(0, 10);

  if (!other) {
    other = {
      industry: 'Other',
      pnl: 0,
      long: 0,
      short: 0,
      value: 0,
      allocation: 0,
    };
  }

  other = industryData.reduce((acc, i) => {
    acc.pnl += i.pnl;
    acc.long += i.long;
    acc.short += i.short;
    acc.value += i.value;
    acc.allocation += i.allocation;
    return acc;
  }, other);

  topTen.push(other);

  return topTen;
};

export const equityPnlBySector = (state: StoreState) => {
  const assets = underlyingAssets(state);
  const instruments = financialInstrumentsList(state);
  const indexedCurrencies = currencies(state);
  const session = activeSession(state);
  const reportingCurrency = indexedCurrencies[session.reporting_currency];

  const equities = instruments.filter(i => {
    const asset = assets[i.underlying_asset];
    if (!asset) {
      return false;
    }
    return ['Equity'].includes(asset.resourcetype);
  });

  const indexedSectors: {
    [key: string]: {
      pnl: number;
      short: number;
      long: number;
      value: number;
      allocation: number;
    };
  } = {};

  const getDollarValue = (instrument: types.FinancialInstrument) => {
    const asset = assets[instrument.underlying_asset];
    const ccy = indexedCurrencies[asset.base_currency];

    return utils.reportingValue(
      instrument,
      ccy,
      asset.resourcetype,
      Object.values(assets),
      Object.values(indexedCurrencies),
      reportingCurrency,
    );
  };

  const initialAum = sessionInitialAUM(state.app.active_session)(state);

  equities.forEach(instrument => {
    const {
      underlying_asset,
      reporting_unrealized_pnl,
      reporting_realized_pnl,
    } = instrument;

    const sector = assets[underlying_asset]?.sector ?? 'Other';
    if (indexedSectors[sector] === undefined) {
      indexedSectors[sector] = {
        pnl: 0,
        long: 0,
        short: 0,
        value: 0,
        allocation: 0,
      };
    }

    const pnl = reporting_unrealized_pnl + reporting_realized_pnl;
    indexedSectors[sector].pnl += pnl;

    const dollarValue = getDollarValue(instrument);
    const valueKey = dollarValue >= 0 ? 'long' : 'short';
    indexedSectors[sector][valueKey] += dollarValue;
    indexedSectors[sector].value += dollarValue;
  });

  const sectorData = Object.entries(indexedSectors)
    .map(([sector, { value, ...data }]) => {
      return {
        ...data,
        sector,
        value,
        allocation: value / initialAum,
      };
    })
    .filter(s => s.value !== 0 || s.pnl !== 0);

  if (sectorData.length <= 10) {
    return sectorData;
  }

  let other = undefined;
  const otherIndex = sectorData.findIndex(i => i.sector === 'Other');
  if (otherIndex !== -1) {
    other = sectorData.splice(otherIndex, 1)[0];
  }

  sectorData.sort((a, b) => b.value - a.value);

  const topTen = sectorData.splice(0, 10);

  if (!other) {
    other = {
      sector: 'Other',
      pnl: 0,
      long: 0,
      short: 0,
      value: 0,
      allocation: 0,
    };
  }

  other = sectorData.reduce((acc, i) => {
    acc.pnl += i.pnl;
    acc.long += i.long;
    acc.short += i.short;
    acc.value += i.value;
    acc.allocation += i.allocation;
    return acc;
  }, other);

  topTen.push(other);

  return topTen;
};

export const playableCurrencies =
  (instruments: types.FinancialInstrument[]) => (state: StoreState) => {
    const assets = underlyingAssets(state);
    const allCurrencies = currencies(state);

    // const fxInstruments = instruments.filter(
    //   instrument => assets[instrument.underlying_asset].resourcetype === 'FX'
    // );

    const symbols = new Set();

    instruments.forEach(instrument => {
      const asset = assets[instrument.underlying_asset];
      const baseSymbol = allCurrencies[asset.base_currency]?.symbol;
      const varSymbol = allCurrencies[asset.variable_currency]?.symbol;

      if (baseSymbol) {
        symbols.add(baseSymbol);
      }
      if (varSymbol) {
        symbols.add(varSymbol);
      }
    });

    return Array.from(symbols) as string[];
  };
export const playableCurrenciesByAssets = (state: StoreState) => {
  //TODO: why not pull instruments from store?
  const assets = underlyingAssetsList(state);
  const allCurrencies = currencies(state);

  // const fxInstruments = instruments.filter(
  //   instrument => assets[instrument.underlying_asset].resourcetype === 'FX'
  // );

  const ccys = new Set();

  assets.forEach(asset => {
    // const asset = assets[instrument.underlying_asset];
    const base = allCurrencies[asset.base_currency];
    const variable = allCurrencies[asset.variable_currency];

    if (base) {
      ccys.add(base);
    }
    if (variable) {
      ccys.add(variable);
    }
  });

  // return Array.from(symbols) as string[];
  return Array.from(ccys) as Currency[];
};

export const trainersList = (state: StoreState) =>
  Object.values(state.app.trainers.items);
export const trainers = (state: StoreState) => state.app.trainers.items;
export const trainersApi = (state: StoreState) => state.app.trainers;

export const selfInsightsReports = (state: StoreState) => {
  const reportIds = state.app.player.item.insights_reports ?? [];
  const reports = state.app.insightsReports.items;
  return reportIds.map(id => reports[id]);
};

export const creatingInsightsReport = (state: StoreState) => {
  return state.app.insightsReports.creating;
};

export const selfRoadmapItemsList = (state: StoreState) => {
  const player = state.app.player.item;
  return player.roadmap_items
    .map(id => state.app.roadmapItems.items[id])
    .filter(item => item !== undefined);
};

export const correlationMatrixAPI = (state: StoreState) =>
  state.app.correlationMatrix;
export const correlationMatrix = (state: StoreState) =>
  state.app.correlationMatrix.item;
export const correlationValuesAPI = (state: StoreState) =>
  state.app.correlationValues;
export const correlationValues = (state: StoreState) =>
  state.app.correlationValues.items;
export const correlationValuesWaitingList = (state: StoreState) =>
  state.app.correlationValuesWaitingList;

export const marketHistoryAPI = (state: StoreState) => state.app.market_history;
export const marketHistory = (state: StoreState) => {
  return state.app.market_history.items;
};
export const marketHistoryList = (state: StoreState) => {
  return Object.values(marketHistory(state));
};

export const stockSplitsAPI = (state: StoreState) => state.app.stock_splits;
export const stockSplits = (state: StoreState) => {
  return state.app.stock_splits.items;
};
export const stockSplitsList = (state: StoreState) => {
  return Object.values(stockSplits(state));
};

export const historicalStep = (state: StoreState) => {
  // no longer used, switched to session.historical_sleep_time
  return state.app.historical_period_length;
};

export const indexFundPreviewData = (state: StoreState) => {
  return state.app.index_fund_preview;
};

export const assetEodData = (state: StoreState) => {
  return state.app.asset_eod_preview;
};

export const eodPriceSegments = (state: StoreState) => {
  return state.app.eod_price_segments.items;
};

export const assetIntradayData = (state: StoreState) => {
  return state.app.asset_intraday_preview;
};

export const auctionsListSelector = (state: StoreState) => {
  return state.app.auctions;
};

export const getAuctionById =
  (auction_id: number | undefined) => (state: StoreState) => {
    return state.app.auctions.find(auction => auction.id === auction_id);
  };

export const getYields = (state: StoreState) => {
  return state.app.sessions.items[state.app.active_session]
    .todays_historical_yields;
};

export const getClientMoney = (state: StoreState) => state.app.client_money;

export const getClientProjections = (state: StoreState) => {
  const futureLoans = Object.values(state.app.client_money.future_loans.items);
  const futureDepos = Object.values(state.app.client_money.future_depos.items);

  let minLoans = 0;
  let maxLoans = 0;
  let minDepos = 0;
  let maxDepos = 0;

  futureLoans.forEach(loan => {
    minLoans = minLoans + loan.min;
    maxLoans = maxLoans + loan.max;
  });
  futureDepos.forEach(depo => {
    minDepos = minDepos + depo.min;
    maxDepos = maxDepos + depo.max;
  });
  // return {
  //   minLoans: minLoans,
  //   maxLoans: maxLoans,
  //   minDepos: minDepos,
  //   maxDepos: maxDepos,
  // };
  return [
    { type: 'Min. Loans', amount: minLoans },
    { type: 'Max. Loans', amount: maxLoans },
    { type: 'Min. Depos', amount: minDepos },
    { type: 'Max. Depos', amount: maxDepos },
  ];
};

export const totalCurrentLoans = (state: StoreState) => {
  const currentLoans = Object.values(
    state.app.client_money.current_loans.items,
  );
  let totalCurrentLoans = 0;
  currentLoans.forEach(loan => {
    totalCurrentLoans += loan.awarded_sum;
  });
  return totalCurrentLoans;
};

export const totalPastLoansAmountAndInterest = (state: StoreState) => {
  const pastLoans = Object.values(state.app.client_money.past_loans.items);
  let totalPastLoansAmount = 0;
  let totalPastLoansInterest = 0;
  pastLoans.forEach(loan => {
    totalPastLoansAmount += loan.awarded_sum;
    totalPastLoansInterest += loan.interest;
  });
  return [totalPastLoansAmount, totalPastLoansInterest];
};

export const totalCurrentDepos = (state: StoreState) => {
  const currentDepos = Object.values(
    state.app.client_money.current_depos.items,
  );
  let totalCurrentDepos = 0;
  currentDepos.forEach(depo => {
    totalCurrentDepos += depo.awarded_sum;
  });
  return totalCurrentDepos;
};

export const totalPastDeposAmountAndInterest = (state: StoreState) => {
  const pastDepos = Object.values(state.app.client_money.past_depos.items);
  let totalPastDeposAmount = 0;
  let totalPastDeposInterest = 0;
  pastDepos.forEach(depo => {
    totalPastDeposAmount += depo.awarded_sum;
    totalPastDeposInterest += depo.interest;
  });
  return [totalPastDeposAmount, totalPastDeposInterest];
};

export const clientsFetched = (state: StoreState) => {
  return state.app.client_money.fetched;
};

export const todaysYields = (state: StoreState) => {
  return state.app.sessions.items[state.app.active_session]
    .todays_historical_yields;
};

export const pastYields = (state: StoreState) => {
  return state.app.sessions.items[state.app.active_session]
    .past_historical_yields;
};

export const executionErrorsSelector = (state: StoreState) => {
  return state.app.errors_tab.execution_errors;
};

export const triedAndFailedOrdersSelector = (state: StoreState) => {
  return state.app.errors_tab.tried_and_failed_orders;
};

export const playersWithDifferencesSelector = (state: StoreState) => {
  return state.app.errors_tab.players_with_differences;
};

export const pendingMoosSelector = (state: StoreState) => {
  return state.app.errors_tab.pending_moos;
};

export const moosExecutedTodaySelector = (state: StoreState) => {
  return state.app.errors_tab.moos_executed_today;
};

export const pendingSLTPsSelector = (state: StoreState) => {
  return state.app.errors_tab.pending_sl_tps;
};

export const marketHoursItems = (state: StoreState) =>
  Object.values(state.app.market_hours.items);

export const marketHoursFetched = (state: StoreState) =>
  state.app.market_hours.fetched;

export const marketHoursFetching = (state: StoreState) =>
  Boolean(state.app.market_hours.fetching);

export const marketHours = (state: StoreState) => {
  return state.app.market_hours.items;
};

export const assetMarketHours =
  (asset: UnderlyingAsset) => (state: StoreState) => {
    return state.app.market_hours.items[asset.market_hours];
  };

export const lastOpenPrices = (state: StoreState) => {
  return Object.values(state.app.last_open_prices.items);
};

export const lastOpenPricesHeader = (state: StoreState) => {
  return state.app.last_open_prices.header;
};

export const lastOpenPricesFetching = (state: StoreState) => {
  return state.app.last_open_prices.fetching;
};

export const lastOpenPricesFetched = (state: StoreState) => {
  return state.app.last_open_prices.fetched;
};

export const lastClosePrices = (state: StoreState) => {
  return Object.values(state.app.last_close_prices.items);
};

export const lastClosePricesHeader = (state: StoreState) => {
  return state.app.last_close_prices.header;
};

export const lastClosePricesFetching = (state: StoreState) => {
  return state.app.last_close_prices.fetching;
};

export const marketHistoryCsvFetching = (state: StoreState) => {
  return state.app.market_history_download_csv.fetching;
};

export const marketHistoryCsv = (state: StoreState) => {
  return Object.values(state.app.market_history_download_csv.items);
};

export const getAllPersons = (state: StoreState) => {
  return state.app.persons.items;
};

export const assetOptionsContractsOld =
  (assetId: number) => (state: StoreState) => {
    return state.app.underlying_assets.items[assetId].options_contracts;
  };

export const assetOptionsContracts =
  (assetId: number) => (state: StoreState) => {
    return state.app.options_contracts_data.of_asset[assetId].contracts;
  };

export const underlyingSpot = (assetId: number) => (state: StoreState) => {
  // return state.app.underlying_assets.items[assetId].underlying_spot;
  return state.app.options_contracts_data.of_asset[assetId].underlying_spot;
};

export const getExpirationDates = (assetId: number) => (state: StoreState) => {
  // return state.app.underlying_assets.items[assetId].expiration_dates || [];
  return state.app.options_contracts_data.of_asset[assetId].expiration_dates;
};

export const getSelectedOptionsContract = (state: StoreState) => {
  return state.app.player.item.selectedOptionsContract;
};

export const optionsInstrumentsList = (state: StoreState) => {
  return Object.values(state.app.player.item.options_instruments.items);
};
export const optionsInstruments = (state: StoreState) => {
  return state.app.player.item.options_instruments.items;
};
// export const optionsAssets = (state: StoreState) => {
//   return state.app.
// };
export const optionChainFetched = (state: StoreState) => {
  return state.app.options_contracts_data.fetched;
};
export const optionChainFetching = (state: StoreState) => {
  return state.app.options_contracts_data.fetching;
};
export const optionsInstrumentsFetching = (state: StoreState) => {
  return state.app.player.item.options_instruments.fetching;
};
export const optionsInstrumentsFetched = (state: StoreState) => {
  return state.app.player.item.options_instruments.fetched;
};

export const ClientDealingScenarios = (state: StoreState) => {
  return state.app.client_dealing_scenarios.items;
};

export const clientDealingScenariosWaiting = (state: StoreState) => {
  return state.app.client_dealing_scenarios.fetching;
};

export const downloadedScenarioData = (state: StoreState) => {
  return state.app.downloaded_scenario;
};

export const fetchedYieldsDownload = (state: StoreState) => {
  return state.app.yield_download.fetched;
};

export const downloadYieldsData = (state: StoreState) => {
  return state.app.yield_download.items;
};

export const currentlyPassingTimeSelector = (state: StoreState) => {
  return state.app.sessions.items[state.app.active_session]
    .currently_passing_time;
};

// export const clientStatsFetched = (state: StoreState) => {
//   return state.app.sessions.items[state.app.active_session]
//     .fetched_client_dealing_stats;
// };

export const clientStatsFetched = (state: StoreState) => {
  return (
    Object.values(state.app.players.items)[0]?.interbank_amount !== undefined
  );
};

// Assessment module (state.app.assessment — pm.api /assessment_sessions/...)
export const assessment = (state: StoreState) => state.app.assessment;
export const assessmentAttached = (state: StoreState) =>
  state.app.assessment.attached;
export const assessmentFetched = (state: StoreState) =>
  state.app.assessment.fetched;
export const assessmentInstruments = (state: StoreState) =>
  state.app.assessment.instruments;
// Active (non-optimistically-revoked) assignments only.
export const assessmentAssignments = (state: StoreState) =>
  Object.values(state.app.assessment.assignments).filter(a => !a.revoked_at);
export const assessmentSliders = (state: StoreState) =>
  state.app.assessment.sliders;
export const assessmentRisk = (state: StoreState) => state.app.assessment.risk;
export const assessmentResults = (state: StoreState) =>
  state.app.assessment.results;
export const assessmentResultDetails = (state: StoreState) =>
  state.app.assessment.resultDetails;
export const assessmentScenarios = (state: StoreState) =>
  state.app.assessment.scenarios;
export const assessmentAttaching = (state: StoreState) =>
  state.app.assessment.attaching;
// Admin Debug tab (envelope + per-player ledgers) and scenario validation.
export const assessmentDebug = (state: StoreState) =>
  state.app.assessment.debug;
export const assessmentValidation = (state: StoreState) =>
  state.app.assessment.validation;
// Admin operations card on the Debug tab (rematch news / recompute optimal /
// bind blueprint).
export const assessmentOperations = (state: StoreState) =>
  state.app.assessment.operations;
